import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Spin,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  DatePicker,
  Segmented,
  Space,
  Empty,
  Breadcrumb,
  Button,
  Grid,
  Divider
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  WalletOutlined,
  RightOutlined,
  ReloadOutlined,
  CalendarOutlined,
  LineChartOutlined,
  InfoCircleOutlined,
  BankOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { walletService } from '../../common/services';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  Area
} from 'recharts';

dayjs.locale('fr');
dayjs.extend(weekOfYear);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Couleurs du thème
const COLORS = {
  entrees: '#52c41a',
  sorties: '#ff4d4f',
  net: '#1890ff',
  solde: '#722ed1',
};

const Wallet = () => {
  const [soldeWallet, setSoldeWallet] = useState(null);
  const [flux, setFlux] = useState({ entrees: 0, sorties: 0, net: 0 });
  const [isLoading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);

  // Contrôles du graphe
  const [granularity, setGranularity] = useState('day');
  const [range, setRange] = useState([
    dayjs().subtract(29, 'day').startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [evolution, setEvolution] = useState([]);

  // Breakpoints
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;

  // Chargement des KPI
  const loadKpis = async () => {
    setLoading(true);
    try {
      const [solde, fluxRes] = await Promise.all([
        walletService.getSoldeWallet(),
        walletService.getFluxWallet({
          from: range[0].format('YYYY-MM-DD'),
          to: range[1].format('YYYY-MM-DD')
        }),
      ]);
      setSoldeWallet(Number(solde?.solde ?? 0));
      setFlux({
        entrees: Number(fluxRes?.entrees ?? 0),
        sorties: Number(fluxRes?.sorties ?? 0),
        net: Number(fluxRes?.net ?? 0),
      });
    } catch (e) {
      console.error('Erreur API (KPIs):', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKpis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch graphe
  useEffect(() => {
    const loadEvolution = async () => {
      setChartLoading(true);
      try {
        const resp = await walletService.getEvolutionWallet({
          from: range[0].format('YYYY-MM-DD'),
          to: range[1].format('YYYY-MM-DD'),
          granularity,
        });
        setEvolution(Array.isArray(resp?.data) ? resp.data : []);
      } catch (e) {
        console.error('Erreur API (Evolution):', e);
        setEvolution([]);
      } finally {
        setChartLoading(false);
      }
    };
    loadEvolution();
  }, [granularity, range]);

  // Formatage pour l'axe X
  const formatLabel = (isoDate) => {
    const d = dayjs(isoDate);
    if (!d.isValid()) return '';
    if (granularity === 'day') return d.format('DD MMM');
    if (granularity === 'week') return `S${d.week()} ${d.format('YY')}`;
    return d.format('MMM YY');
  };

  const chartData = useMemo(
    () => evolution.map((r) => ({
      x: formatLabel(r.date),
      fullDate: r.date,
      entrees: Math.abs(Number(r.entrees || 0)),
      sorties: Math.abs(Number(r.sorties || 0)),
      net: Number(r.net || 0),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [evolution, granularity]
  );

  // Formatage des nombres
  const formatNumber = (v) => {
    if (v === null || v === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(v);
  };

  const formatCompact = (v) => {
    if (v === null || v === undefined) return '—';
    const absV = Math.abs(v);
    if (absV >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (absV >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return formatNumber(v);
  };

  // Tooltip personnalisé pour le graphique
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const dataPoint = payload[0]?.payload;
    return (
      <Card
        size="small"
        style={{
          pointerEvents: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: 'none',
          borderRadius: 8,
        }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
          {dataPoint?.fullDate ? dayjs(dataPoint.fullDate).format('dddd DD MMMM YYYY') : label}
        </Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: COLORS.entrees }} />
            <Text>Entrées: <Text strong style={{ color: COLORS.entrees }}>{formatNumber(dataPoint?.entrees)} pts</Text></Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: COLORS.sorties }} />
            <Text>Sorties: <Text strong style={{ color: COLORS.sorties }}>{formatNumber(dataPoint?.sorties)} pts</Text></Text>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: COLORS.net }} />
            <Text>Net: <Text strong style={{ color: dataPoint?.net >= 0 ? COLORS.entrees : COLORS.sorties }}>{formatNumber(dataPoint?.net)} pts</Text></Text>
          </div>
        </div>
      </Card>
    );
  };

  // Configuration des cartes KPI
  const kpiCards = [
    {
      title: 'Sorties',
      value: flux.sorties,
      icon: <ArrowDownOutlined />,
      color: COLORS.sorties,
      bgGradient: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
      tooltip: 'Points donnés aux chauffeurs (débit du wallet)',
      trend: 'down',
    },
    {
      title: 'Entrées',
      value: flux.entrees,
      icon: <ArrowUpOutlined />,
      color: COLORS.entrees,
      bgGradient: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
      tooltip: 'Points retirés aux chauffeurs (crédit du wallet)',
      trend: 'up',
    },
    {
      title: 'Net (Période)',
      value: flux.net,
      icon: <SwapOutlined />,
      color: flux.net >= 0 ? COLORS.entrees : COLORS.sorties,
      bgGradient: flux.net >= 0
        ? 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)'
        : 'linear-gradient(135deg, #fff2e8 0%, #ffd8bf 100%)',
      tooltip: 'Entrées - Sorties sur la période sélectionnée',
      trend: flux.net >= 0 ? 'up' : 'down',
    },
    {
      title: 'Solde Actuel',
      value: soldeWallet,
      icon: <WalletOutlined />,
      color: (soldeWallet ?? 0) >= 0 ? COLORS.solde : COLORS.sorties,
      bgGradient: (soldeWallet ?? 0) >= 0
        ? 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)'
        : 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
      tooltip: 'Solde courant du wallet',
      highlight: true,
    },
  ];

  return (
    <Spin spinning={isLoading} tip="Chargement...">
      <div style={{ minHeight: '100vh' }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined />}
          style={{ marginBottom: 20 }}
          items={[
            { title: <Link to="/dashboard">Accueil</Link> },
            { title: <span>Administration</span> },
            { title: <span style={{ fontWeight: 600 }}>Wallet</span> },
          ]}
        />

        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}>
          <div>
            <Title level={isMobile ? 4 : 3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
              <BankOutlined style={{ color: '#722ed1' }} />
              Solde du Wallet
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>
              Suivi des entrées, sorties et solde du wallet Tigoon
            </Text>
          </div>
          <Button
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={loadKpis}
            disabled={isLoading}
          >
            {!isMobile && 'Actualiser'}
          </Button>
        </div>

        {/* KPI Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {kpiCards.map((card, index) => (
            <Col xs={12} sm={12} md={6} key={index}>
              <Card
                hoverable
                style={{
                  background: card.bgGradient,
                  border: card.highlight ? '2px solid #722ed1' : 'none',
                  borderRadius: 16,
                  height: '100%',
                  overflow: 'hidden',
                }}
                bodyStyle={{ padding: isMobile ? 16 : 20 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: isMobile ? 12 : 13,
                          fontWeight: 500,
                        }}
                      >
                        {card.title}
                      </Text>
                      <Tooltip title={card.tooltip}>
                        <InfoCircleOutlined style={{ fontSize: 12, color: '#8c8c8c', cursor: 'help' }} />
                      </Tooltip>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
                      <Text
                        style={{
                          fontSize: isMobile ? 22 : 28,
                          fontWeight: 700,
                          color: card.color,
                          lineHeight: 1.2,
                        }}
                      >
                        {card.trend === 'down' && card.value > 0 && '↓ '}
                        {card.trend === 'up' && card.value > 0 && '↑ '}
                        {formatCompact(card.value)}
                      </Text>
                      <Text
                        style={{
                          fontSize: isMobile ? 12 : 14,
                          color: card.color,
                          opacity: 0.8,
                        }}
                      >
                        pts
                      </Text>
                    </div>
                  </div>
                  <div
                    style={{
                      width: isMobile ? 40 : 52,
                      height: isMobile ? 40 : 52,
                      borderRadius: 12,
                      backgroundColor: 'rgba(255,255,255,0.85)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? 20 : 24,
                      color: card.color,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Graphique */}
        <Card
          style={{ borderRadius: 16 }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Header du graphique */}
          <div
            style={{
              padding: isMobile ? '16px 16px 12px' : '20px 24px 16px',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col xs={24} lg={8}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LineChartOutlined style={{ color: '#fff', fontSize: 20 }} />
                  </div>
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Évolution du Wallet
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Visualisation des flux sur la période
                    </Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} lg={16}>
                <Space
                  size="middle"
                  wrap
                  style={{
                    display: 'flex',
                    justifyContent: isMobile ? 'flex-start' : 'flex-end',
                    width: '100%',
                  }}
                >
                  <Segmented
                    size={isMobile ? 'small' : 'middle'}
                    options={[
                      { label: 'Jour', value: 'day' },
                      { label: 'Semaine', value: 'week' },
                      { label: 'Mois', value: 'month' },
                    ]}
                    value={granularity}
                    onChange={setGranularity}
                  />
                  <RangePicker
                    size={isMobile ? 'small' : 'middle'}
                    value={range}
                    onChange={(vals) => {
                      if (!vals || vals.length !== 2) return;
                      setRange([vals[0].startOf('day'), vals[1].endOf('day')]);
                    }}
                    allowClear={false}
                    format="DD/MM/YYYY"
                    suffixIcon={<CalendarOutlined />}
                    style={{ minWidth: isMobile ? 220 : 260 }}
                  />
                </Space>
              </Col>
            </Row>
          </div>

          {/* Légende personnalisée */}
          <div
            style={{
              padding: '12px 24px',
              display: 'flex',
              justifyContent: 'center',
              gap: isMobile ? 16 : 32,
              flexWrap: 'wrap',
              borderBottom: '1px solid #f5f5f5',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                backgroundColor: COLORS.entrees,
              }} />
              <Text style={{ fontSize: 13 }}>Entrées</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 16,
                height: 16,
                borderRadius: 4,
                backgroundColor: COLORS.sorties,
              }} />
              <Text style={{ fontSize: 13 }}>Sorties</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 16,
                height: 3,
                backgroundColor: COLORS.net,
                borderRadius: 2,
              }} />
              <Text style={{ fontSize: 13 }}>Net</Text>
            </div>
          </div>

          {/* Graphique */}
          <div style={{ padding: isMobile ? 12 : 24 }}>
            <Spin spinning={chartLoading}>
              {chartData.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text type="secondary">
                      Aucune donnée disponible sur cette période
                    </Text>
                  }
                  style={{ padding: '60px 0' }}
                />
              ) : (
                <div style={{ width: '100%', height: isMobile ? 280 : 380 }}>
                  <ResponsiveContainer>
                    <ComposedChart
                      data={chartData}
                      margin={{
                        top: 10,
                        right: isMobile ? 10 : 30,
                        left: isMobile ? -10 : 10,
                        bottom: 10,
                      }}
                    >
                      <defs>
                        <linearGradient id="colorEntrees" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.entrees} stopOpacity={0.9} />
                          <stop offset="95%" stopColor={COLORS.entrees} stopOpacity={0.6} />
                        </linearGradient>
                        <linearGradient id="colorSorties" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={COLORS.sorties} stopOpacity={0.9} />
                          <stop offset="95%" stopColor={COLORS.sorties} stopOpacity={0.6} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#f0f0f0"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="x"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: isMobile ? 10 : 12, fill: '#8c8c8c' }}
                        tickMargin={10}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatCompact}
                        tick={{ fontSize: isMobile ? 10 : 12, fill: '#8c8c8c' }}
                        tickMargin={10}
                      />
                      <RTooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="sorties"
                        name="Sorties"
                        fill="url(#colorSorties)"
                        radius={[4, 4, 0, 0]}
                        barSize={isMobile ? 12 : 20}
                      />
                      <Bar
                        dataKey="entrees"
                        name="Entrées"
                        fill="url(#colorEntrees)"
                        radius={[4, 4, 0, 0]}
                        barSize={isMobile ? 12 : 20}
                      />
                      <Line
                        type="monotone"
                        dataKey="net"
                        name="Net"
                        stroke={COLORS.net}
                        strokeWidth={3}
                        dot={{ fill: COLORS.net, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Spin>
          </div>

          {/* Footer stats résumé */}
          {chartData.length > 0 && (
            <div
              style={{
                padding: '16px 24px',
                background: '#fafafa',
                borderTop: '1px solid #f0f0f0',
                borderRadius: '0 0 16px 16px',
              }}
            >
              <Row gutter={[24, 12]} justify="center">
                <Col>
                  <Statistic
                    title={<Text type="secondary" style={{ fontSize: 12 }}>Total Entrées</Text>}
                    value={chartData.reduce((sum, d) => sum + d.entrees, 0)}
                    valueStyle={{ fontSize: 16, color: COLORS.entrees }}
                    suffix="pts"
                    formatter={formatNumber}
                  />
                </Col>
                <Col>
                  <Divider type="vertical" style={{ height: 40 }} />
                </Col>
                <Col>
                  <Statistic
                    title={<Text type="secondary" style={{ fontSize: 12 }}>Total Sorties</Text>}
                    value={chartData.reduce((sum, d) => sum + d.sorties, 0)}
                    valueStyle={{ fontSize: 16, color: COLORS.sorties }}
                    suffix="pts"
                    formatter={formatNumber}
                  />
                </Col>
                <Col>
                  <Divider type="vertical" style={{ height: 40 }} />
                </Col>
                <Col>
                  <Statistic
                    title={<Text type="secondary" style={{ fontSize: 12 }}>Moyenne Net/Jour</Text>}
                    value={chartData.length > 0 ? chartData.reduce((sum, d) => sum + d.net, 0) / chartData.length : 0}
                    valueStyle={{
                      fontSize: 16,
                      color: chartData.reduce((sum, d) => sum + d.net, 0) >= 0 ? COLORS.entrees : COLORS.sorties
                    }}
                    suffix="pts"
                    precision={0}
                    formatter={formatNumber}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Card>
      </div>
    </Spin>
  );
};

export default Wallet;
